/* Copy da área `components/support/`: chat de atendimento (header, composer, thread, bolha,
   indicador de digitação), modais de atender/fechar ticket, badge de conexão realtime, painel
   de contexto (ticket/usuário/organização), toolbar de filtros e tabela da inbox, e respostas
   rápidas do composer. As páginas-rota (`support-chat-page.tsx`/inbox) têm as próprias áreas
   (`support-chat.ts`/`support-inbox.ts`) — fora do escopo deste arquivo. */
import type { TicketDto } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';
type PriorityFilterKey = 'all' | TicketDto['priority'];
type AssignmentFilterKey = 'all' | 'mine' | 'unassigned';
type PeriodFilterKey = 'all' | '24h' | '7d' | '30d';
type SegmentFilterKey = 'all' | 'org' | 'individual';
type QuickTemplateId = 'refund' | 'plan' | 'logged';

/** Chave estável de erro de operação em tickets — o texto mora só aqui. `default` cobre
    qualquer `ApiError` cujo código não tem mensagem específica. */
export type TicketErrorKey = 'notFound' | 'network' | 'default' | 'unexpected';

/** Rótulo por status de ticket. Campo próprio (não `Record<TicketStatus, string>`) porque
    o status `in_progress` do contrato Zod (`TicketDto['status']`) é snake_case — chave de
    objeto continuaria camelCase e a leitura vira `switch` em vez de indexação por bracket. */
export interface TicketStatusLabels {
  readonly open: string;
  readonly inProgress: string;
  readonly closed: string;
}

interface SupportContent {
  readonly assignModal: {
    readonly title: string;
    readonly description: string;
    readonly cancel: string;
    readonly confirm: string;
    readonly successToast: string;
  };
  readonly closeModal: {
    readonly title: string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly reasonLabel: string;
    readonly successToast: string;
  };
  readonly chatHeader: {
    readonly assign: string;
    readonly close: string;
  };
  readonly chatComposer: {
    readonly placeholder: string;
    readonly messageAriaLabel: string;
    readonly sendAriaLabel: string;
  };
  readonly chatMessage: {
    readonly sendingSuffix: string;
  };
  readonly chatThread: {
    readonly empty: string;
  };
  readonly connectionBadge: {
    readonly ariaLabel: string;
    readonly states: Readonly<Record<ConnectionState, string>>;
  };
  readonly inboxFilters: {
    readonly searchAriaLabel: string;
    readonly searchPlaceholder: string;
    readonly statusAriaLabel: string;
    readonly statusPlaceholder: string;
    readonly statusOptions: { readonly all: string } & TicketStatusLabels;
    readonly priorityAriaLabel: string;
    readonly priorityPlaceholder: string;
    readonly priorityOptions: Readonly<Record<PriorityFilterKey, string>>;
    readonly assignmentAriaLabel: string;
    readonly assignmentOptions: Readonly<Record<AssignmentFilterKey, string>>;
    readonly periodAriaLabel: string;
    readonly periodOptions: Readonly<Record<PeriodFilterKey, string>>;
    readonly segmentAriaLabel: string;
    readonly segmentOptions: Readonly<Record<SegmentFilterKey, string>>;
  };
  readonly inboxTable: {
    readonly emptyFilteredTitle: string;
    readonly emptyFilteredDescription: string;
    readonly clearFilters: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly columnSubject: string;
    readonly columnUser: string;
    readonly columnPriority: string;
    readonly columnCreatedAt: string;
    readonly columnAssignee: string;
    readonly columnStatus: string;
    readonly userIdLabel: string;
    readonly noAssignee: string;
  };
  readonly quickActions: {
    readonly triggerAriaLabel: string;
    readonly templates: Readonly<
      Record<QuickTemplateId, { readonly label: string; readonly text: string }>
    >;
  };
  readonly contextPanel: {
    readonly loadingAriaLabel: string;
    readonly tabTicket: string;
    readonly tabUser: string;
    readonly tabOrg: string;
    readonly userLoadError: string;
    readonly userUnavailable: string;
    readonly orgIndividualPlaceholder: string;
    readonly orgLoadError: string;
    readonly orgUnavailable: string;
    readonly fieldEmail: string;
    readonly fieldName: string;
    readonly fieldOrgs: string;
    readonly fieldLicenses: string;
    readonly fieldStatus: string;
    readonly fieldMembers: string;
    readonly fieldSubscription: string;
    readonly fieldSubject: string;
    readonly fieldPriority: string;
    readonly fieldCreatedAt: string;
    readonly fieldAssignedTo: string;
    readonly fieldOrigin: string;
    readonly noAssignee: string;
    readonly originIndividual: string;
    readonly originOrg: string;
  };
  /** Consumido por `ticket-error.ts` (fora de componente — sem acesso ao hook). */
  readonly ticketErrors: Readonly<Record<TicketErrorKey, string>>;
  /** Consumido por `ticket-status.ts` (fora de componente — sem acesso ao hook). */
  readonly ticketStatusLabels: TicketStatusLabels;
  readonly typingIndicator: {
    readonly singular: string;
    /** "N pessoas digitando…" — plural sempre com contagem, nunca concatenação solta. */
    readonly plural: (count: number) => string;
  };
}

export const SUPPORT_CONTENT: Bilingual<SupportContent> = {
  'pt-BR': {
    assignModal: {
      title: 'Atender este ticket?',
      description: 'Você ficará responsável por este atendimento.',
      cancel: 'Cancelar',
      confirm: 'Atender',
      successToast: 'Ticket atribuído a você.',
    },
    closeModal: {
      title: 'Fechar ticket?',
      description: 'Marca o atendimento como concluído. O usuário pode reabrir respondendo.',
      confirmLabel: 'Fechar ticket',
      reasonLabel: 'Resumo do desfecho (mín. 10 caracteres)',
      successToast: 'Ticket fechado.',
    },
    chatHeader: {
      assign: 'Atender',
      close: 'Fechar',
    },
    chatComposer: {
      placeholder: 'Digite uma mensagem…',
      messageAriaLabel: 'Mensagem',
      sendAriaLabel: 'Enviar',
    },
    chatMessage: {
      sendingSuffix: ' · enviando…',
    },
    chatThread: {
      empty: 'Sem mensagens ainda.',
    },
    connectionBadge: {
      ariaLabel: 'Status da conexão em tempo real',
      states: {
        open: 'Conectado',
        connecting: 'Conectando…',
        reconnecting: 'Reconectando…',
        closed: 'Desconectado',
      },
    },
    inboxFilters: {
      searchAriaLabel: 'Buscar tickets',
      searchPlaceholder: 'Buscar por assunto…',
      statusAriaLabel: 'Filtrar status',
      statusPlaceholder: 'Status',
      statusOptions: {
        all: 'Todos',
        open: 'Aberto',
        inProgress: 'Em atendimento',
        closed: 'Fechado',
      },
      priorityAriaLabel: 'Filtrar prioridade',
      priorityPlaceholder: 'Prioridade',
      priorityOptions: {
        all: 'Todas',
        urgent: 'Urgente',
        high: 'Alta',
        normal: 'Normal',
        low: 'Baixa',
      },
      assignmentAriaLabel: 'Filtrar por atribuição',
      assignmentOptions: { all: 'Todos', mine: 'Meus', unassigned: 'Não atribuídos' },
      periodAriaLabel: 'Filtrar por período',
      periodOptions: { all: 'Todos', '24h': '24h', '7d': '7 dias', '30d': '30 dias' },
      segmentAriaLabel: 'Filtrar por origem',
      segmentOptions: { all: 'Todos', org: 'Org', individual: 'Individual' },
    },
    inboxTable: {
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhum ticket corresponde aos filtros.',
      clearFilters: 'Limpar filtros',
      emptyTitle: 'Nenhum ticket',
      emptyDescription: 'Os tickets de suporte aparecem aqui.',
      columnSubject: 'Assunto',
      columnUser: 'Usuário',
      columnPriority: 'Prioridade',
      columnCreatedAt: 'Aberto em',
      columnAssignee: 'Atendente',
      columnStatus: 'Status',
      userIdLabel: 'id do usuário',
      noAssignee: 'Nenhum',
    },
    quickActions: {
      triggerAriaLabel: 'Respostas rápidas',
      templates: {
        refund: {
          label: 'Reembolso emitido',
          text: 'Olá! Processamos o reembolso — o crédito aparece em 5 a 10 dias úteis.',
        },
        plan: {
          label: 'Plano alterado',
          text: 'Atualizamos seu plano; as próximas cobranças já refletem o novo valor.',
        },
        logged: {
          label: 'Issue registrada',
          text: 'Obrigado pelo report! Registramos o problema e avisaremos quando houver correção.',
        },
      },
    },
    contextPanel: {
      loadingAriaLabel: 'Carregando',
      tabTicket: 'Ticket',
      tabUser: 'Usuário',
      tabOrg: 'Organização',
      userLoadError: 'Falha ao carregar o usuário.',
      userUnavailable: 'Usuário indisponível.',
      orgIndividualPlaceholder: 'Ticket individual — sem organização vinculada.',
      orgLoadError: 'Falha ao carregar a organização.',
      orgUnavailable: 'Organização indisponível.',
      fieldEmail: 'Email',
      fieldName: 'Nome',
      fieldOrgs: 'Organizações',
      fieldLicenses: 'Licenses',
      fieldStatus: 'Status',
      fieldMembers: 'Membros',
      fieldSubscription: 'Assinatura',
      fieldSubject: 'Assunto',
      fieldPriority: 'Prioridade',
      fieldCreatedAt: 'Criado em',
      fieldAssignedTo: 'Atribuído',
      fieldOrigin: 'Origem',
      noAssignee: 'Ninguém',
      originIndividual: 'Individual',
      originOrg: 'Organização',
    },
    ticketErrors: {
      notFound: 'Ticket não encontrado ou já fechado.',
      network: 'Falha de conexão. Tente novamente.',
      default: 'Operação falhou. Tente novamente.',
      unexpected: 'Erro inesperado.',
    },
    ticketStatusLabels: {
      open: 'Aberto',
      inProgress: 'Em atendimento',
      closed: 'Fechado',
    },
    typingIndicator: {
      singular: 'Digitando…',
      plural: (count) => `${count} pessoas digitando…`,
    },
  },
  'en-US': {
    assignModal: {
      title: 'Take this ticket?',
      description: 'You will become responsible for this conversation.',
      cancel: 'Cancel',
      confirm: 'Take ticket',
      successToast: 'Ticket assigned to you.',
    },
    closeModal: {
      title: 'Close ticket?',
      description: 'Marks the conversation as resolved. The user can reopen it by replying.',
      confirmLabel: 'Close ticket',
      reasonLabel: 'Resolution summary (min. 10 characters)',
      successToast: 'Ticket closed.',
    },
    chatHeader: {
      assign: 'Take ticket',
      close: 'Close',
    },
    chatComposer: {
      placeholder: 'Type a message…',
      messageAriaLabel: 'Message',
      sendAriaLabel: 'Send',
    },
    chatMessage: {
      sendingSuffix: ' · sending…',
    },
    chatThread: {
      empty: 'No messages yet.',
    },
    connectionBadge: {
      ariaLabel: 'Realtime connection status',
      states: {
        open: 'Connected',
        connecting: 'Connecting…',
        reconnecting: 'Reconnecting…',
        closed: 'Disconnected',
      },
    },
    inboxFilters: {
      searchAriaLabel: 'Search tickets',
      searchPlaceholder: 'Search by subject…',
      statusAriaLabel: 'Filter status',
      statusPlaceholder: 'Status',
      statusOptions: {
        all: 'All',
        open: 'Open',
        inProgress: 'In progress',
        closed: 'Closed',
      },
      priorityAriaLabel: 'Filter priority',
      priorityPlaceholder: 'Priority',
      priorityOptions: {
        all: 'All',
        urgent: 'Urgent',
        high: 'High',
        normal: 'Normal',
        low: 'Low',
      },
      assignmentAriaLabel: 'Filter by assignment',
      assignmentOptions: { all: 'All', mine: 'Mine', unassigned: 'Unassigned' },
      periodAriaLabel: 'Filter by period',
      periodOptions: { all: 'All', '24h': '24h', '7d': '7 days', '30d': '30 days' },
      segmentAriaLabel: 'Filter by origin',
      segmentOptions: { all: 'All', org: 'Org', individual: 'Individual' },
    },
    inboxTable: {
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No ticket matches the filters.',
      clearFilters: 'Clear filters',
      emptyTitle: 'No tickets',
      emptyDescription: 'Support tickets appear here.',
      columnSubject: 'Subject',
      columnUser: 'User',
      columnPriority: 'Priority',
      columnCreatedAt: 'Opened at',
      columnAssignee: 'Assignee',
      columnStatus: 'Status',
      userIdLabel: 'user id',
      noAssignee: 'None',
    },
    quickActions: {
      triggerAriaLabel: 'Quick replies',
      templates: {
        refund: {
          label: 'Refund issued',
          text: 'Hi! We processed the refund — the credit appears within 5 to 10 business days.',
        },
        plan: {
          label: 'Plan changed',
          text: 'We updated your plan; upcoming charges already reflect the new amount.',
        },
        logged: {
          label: 'Issue logged',
          text: 'Thanks for the report! We logged the issue and will let you know once it is fixed.',
        },
      },
    },
    contextPanel: {
      loadingAriaLabel: 'Loading',
      tabTicket: 'Ticket',
      tabUser: 'User',
      tabOrg: 'Organization',
      userLoadError: 'Failed to load the user.',
      userUnavailable: 'User unavailable.',
      orgIndividualPlaceholder: 'Individual ticket — no organization linked.',
      orgLoadError: 'Failed to load the organization.',
      orgUnavailable: 'Organization unavailable.',
      fieldEmail: 'Email',
      fieldName: 'Name',
      fieldOrgs: 'Organizations',
      fieldLicenses: 'Licenses',
      fieldStatus: 'Status',
      fieldMembers: 'Members',
      fieldSubscription: 'Subscription',
      fieldSubject: 'Subject',
      fieldPriority: 'Priority',
      fieldCreatedAt: 'Created at',
      fieldAssignedTo: 'Assigned to',
      fieldOrigin: 'Origin',
      noAssignee: 'Nobody',
      originIndividual: 'Individual',
      originOrg: 'Organization',
    },
    ticketErrors: {
      notFound: 'Ticket not found or already closed.',
      network: 'Connection failed. Try again.',
      default: 'Operation failed. Try again.',
      unexpected: 'Unexpected error.',
    },
    ticketStatusLabels: {
      open: 'Open',
      inProgress: 'In progress',
      closed: 'Closed',
    },
    typingIndicator: {
      singular: 'Typing…',
      plural: (count) => `${count} people typing…`,
    },
  },
};

export const useSupportContent = (): SupportContent => useBilingual(SUPPORT_CONTENT);
